// ENT-4086 (Codex R10-A / R13-D): ONE transport-fact classifier for every Auth/claim
// boundary in the invite flow. Never keys on message text. `transient` = keep the
// invitee on the route with a retry affordance; `terminal` = the invitation itself is
// unusable (consumed / expired / forbidden / not found / malformed).
export type FailureClass = 'transient' | 'terminal';

/** 4xx statuses that are transport/timing, not a verdict on the invitation. */
export const TRANSIENT_4XX: ReadonlySet<number> = new Set([408, 425, 429]);

/** undefined/0 = no HTTP response (network, relay, fetch failure); 5xx = upstream; 408/425/429 = timing. */
export function isTransientHttpStatus(status: number | undefined): boolean {
  if (status === undefined || status === 0) return true;
  if (status >= 500) return true;
  return TRANSIENT_4XX.has(status);
}

/**
 * Classify a supabase-js Auth error object (the `error` of `{ data, error }`; never thrown).
 * `AuthRetryableFetchError` (auth-js: fetch failure → status 0; 502/503/504) is transient by
 * name so mocks and real instances classify alike; everything else classifies by status.
 * A null/undefined error is `terminal` (callers pass it only when `data.session` is missing).
 */
export function classifyAuthError(error: unknown): FailureClass {
  if (!error || typeof error !== 'object') return 'terminal';
  const e = error as { name?: unknown; status?: unknown };
  if (e.name === 'AuthRetryableFetchError') return 'transient';
  const status = typeof e.status === 'number' ? e.status : undefined;
  return isTransientHttpStatus(status) ? 'transient' : 'terminal';
}
